import React, { useEffect, useState } from "react";
import { Select, Spin } from "antd";
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import "../styles/common-page.css";
import donationImage from "../assets/images/donation.png";
import DonarCard from '../components/DonarCard';
import DownloadCard from '../components/DownloadCard';
import axiosInstance from '../services/axios';
import VillageIllustration from '../components/VillageIllustration';

const Donation = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  // Financial years state
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  // donors state (initialized with empty array; will use API to populate)
  const [donors, setDonors] = useState([]);
  const [loadingDonors, setLoadingDonors] = useState(true);
  const [donorsError, setDonorsError] = useState(null);

  // Fetch financial years from API
  useEffect(() => {
    let mounted = true;
    const fetchFinancialYears = async () => {
      try {
        const res = await axiosInstance.get("/financial-years");
        if (!mounted) return;
        const data = res.data || [];
        if (Array.isArray(data) && data.length > 0) {
          setFinancialYears(data);
          // Default to 2025-2026, fallback to first year
          const defaultYear = data.find((y) => y.year === "2025-2026" || y.name === "2025-2026") || data[0];
          setSelectedYear(defaultYear);
        }
      } catch (err) {
        console.error("Failed to load financial years:", err);
      }
    };
    fetchFinancialYears();
    return () => { mounted = false; };
  }, []);

  // Build select options from API data
  const financialYearOptions = financialYears.map((year) => ({
    value: year.id || year.year,
    label: (
      <span>
        {lang === "en"
          ? `Financial Year ${year.year || year.name}`
          : `आर्थिक वर्ष ${year.year || year.name}`}
      </span>
    ),
  }));

  const handleYearChange = (value) => {
    const year = financialYears.find((y) => (y.id || y.year) === value);
    setSelectedYear(year);
  };

  // Fetch donors based on selected financial year
  useEffect(() => {
    let mounted = true;
    const fetchDonors = async () => {
      setLoadingDonors(true);
      setDonorsError(null);
      try {
        const languageParam = lang === 'mr' ? 'Marathi' : 'English';
        const params = { language: languageParam };

        // Add financial year filter if selected
        if (selectedYear?.id) {
          params.financial_year = selectedYear.id;
        }

        const res = await axiosInstance.get('/donations/', { params });
        if (!mounted) return;
        const data = res.data || [];
        console.log('Donations API response:', data);
        const mapped = (Array.isArray(data) ? data : []).map(item => ({
          donorName: item.donor_name || '',
          amount: item.amount || '',
          description: item.description || '',
          image: item.image || null,
        }));

        setDonors(mapped);
        console.log('Mapped donors:', mapped);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load donations:', err);
        setDonorsError(err);
      } finally {
        if (mounted) setLoadingDonors(false);
      }
    };

    fetchDonors();
    return () => { mounted = false; };
  }, [lang, selectedYear]);

  // Language-specific text content
  const content = {
    en: {
      homepage: 'Homepage',
      title: 'Donation',
      footerAlt: 'Footer Image',
      reportTitle: 'List of Donors',
      reportDescription: 'Download the list of generous individuals who have contributed to the development of the Gram Panchayat through their generous donations. This list contains all the necessary information you need.',
      downloadBtn: 'Download'
    },
    mr: {
      homepage: 'मुख्यपृष्ठ',
      title: 'देणगी',
      footerAlt: 'तळ चित्र',
      reportTitle: 'देणगीदारांची यादी',
      reportDescription: 'दानशूर व्यक्तींची यादी डाउनलोड करा, ज्यांनी आपल्या उदार देणग्यांद्वारे ग्रामपंचायतीच्या विकासाला हातभार लावला आहे. या यादीमध्ये तुम्हाला आवश्यक असणारी सर्व माहिती मिळेल.',
      downloadBtn: 'डाउनलोड करा'
    }
  };

  const currentContent = content[i18n.language] || content.mr;

  return (
    <>
      <div className="bg-image-section">
        <div className="flex flex-col gap-4 section-padding">
          <PageHeader
            breadcrumb={[
              <Link key="home" to="/">{currentContent.homepage}</Link>,
              <span key="donation">{currentContent.title}</span>
            ]}
            title={currentContent.title}
            actions={
              <Select
                className="w-[228px] max-sm:w-full"
                placeholder={lang === "en" ? "Select Financial Year" : "आर्थिक वर्ष निवडा"}
                options={financialYearOptions}
                value={selectedYear?.id || selectedYear?.year}
                onChange={handleYearChange}
              />
            }
          />

          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-sm:gap-4">
              {loadingDonors ? (
                <div key="loading" className="col-span-full text-center py-8"><Spin size="large" /></div>
              ) : donorsError ? (
                <div key="error" className="col-span-full"><EmptyState description={i18n.language === 'en' ? 'Failed to load donors' : 'दान देणाऱ्यांची यादी लोड करण्यात अयशस्वी'} /></div>
              ) : donors.length === 0 ? (
                <div key="empty" className="col-span-full"><EmptyState description={i18n.language === 'en' ? 'No donors found' : 'दान देणारे सापडले नाहीत'} /></div>
              ) : (
                donors.map((donor, index) => (
                  <DonarCard
                    key={index}
                    donationImage={donor.image || donationImage}
                    donorName={donor.donorName}
                    amount={donor.amount}
                    description={donor.description}
                  />
                ))
              )}
            </div>
            <div className="mt-6">
              <DownloadCard
                title={content.mr.reportTitle}
                titleEn={content.en.reportTitle}
                description={content.mr.reportDescription}
                descriptionEn={content.en.reportDescription}
                downloadLink="/reports/residence-declaration.pdf"
                buttonLabel={content.mr.downloadBtn}
                buttonLabelEn={content.en.downloadBtn}
              />
            </div>
          </div>
        </div>
      </div>
      <VillageIllustration />
    </>
  );
};

export default Donation;